/**
 * `collections_list` — the ONLY read on this surface in tranche 1, and it is
 * deliberately the narrowest useful one.
 *
 * ## What it returns, and what it refuses to return
 *
 * Collections, their folder trees, and — with `include_requests` — each
 * request's `{id, name, method}`. **No url, no headers, no body, no auth, no
 * scripts, no mock examples.** Not "redacted": absent, because there is no
 * branch here that reads them.
 *
 * **TRANCHE 2 CHANGES ONE THING: the url.** With `include_requests` each
 * request now carries its url — through `requestProjection.projectUrl`, the
 * same function `request_read` uses, never a second laxer path. So the userinfo
 * is dropped structurally, the query is rebuilt from projected parameters
 * (`?api_key=…` comes back `<hidden>`), and the path runs through
 * `redactString`.
 *
 * The path is the residue and it is named rather than hidden: a credential in a
 * PATH SEGMENT — `https://api.x/v1/9f8a7b…/` — is not a header, not a query
 * parameter and not a `{{template}}`, so no structural rule can tell it from a
 * tenant id. `redactString` (`utils/redactResponse.js:53`) catches ≥32-char
 * hex, ≥40-char base64, JWTs and PEM blocks and nothing shorter. That is
 * best-effort, it is the ONE best-effort step in this contract, and it is why a
 * url waited a tranche.
 *
 * Everything else stays absent rather than redacted: no headers, no body, no
 * auth, no scripts, no mock examples. `collection_read` and `request_read` are
 * where those live, so a listing over a large workspace stays a listing.
 *
 * ## Why there is no `scope` and what stands in its place (**O27**)
 *
 * Collections live in dedicated tables keyed by `workspace_id`, not in the
 * `personal` / `team:<id>` scope vocabulary the other three surfaces share.
 * The unit here is the workspace the user has OPEN, every result names it, and
 * `scopesConsulted` / `scopesUnavailable` are present-but-empty so a client
 * never has to special-case the result shape.
 *
 * `otherWorkspaceCount` is what actually answers the question
 * `scopesUnavailable` exists to answer — "is this the whole picture?". It is a
 * COUNT and never names, because the other workspaces' names are not something
 * this grant was asked for.
 */
import { normalizeParentId } from '../../utils/collectionFolders';
import { useWorkspaceStore } from '../../store/workspaceStore';
import { currentWorkspaceOrRefuse, readCollections, workspaceReport } from '../collectionsDb';
import { foldersOf } from './collectionReads';
import { projectUrl } from '../requestProjection';
import { revOfCollection, revOfRequest } from '../collectionRev';

/**
 * Built field by field and never spread — `{...request}` is phase 2's
 * `board_read` defect, and on this record it would emit every header value and
 * the whole auth block in one character.
 */
function requestRowsOf(collection) {
  return (collection.requests || [])
    .slice()
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .map((r) => ({
      id: String(r.id),
      // The rev the update and mock tools accept, so a model that listed can
      // act without a second read. Same function the writer checks against.
      rev: revOfRequest(r),
      name: String(r.name || ''),
      method: String(r.method || 'GET').toUpperCase(),
      // Through the projection, always. A listing with its own url handling
      // would be a second implementation of the hardest rule in the feature.
      url: projectUrl(r.url, r.params).url,
      folderId: normalizeParentId(r.folder_id),
    }));
}

export async function collectionsList(args = {}) {
  const gate = currentWorkspaceOrRefuse();
  if (gate.error) return gate.error;
  const { workspace } = gate;

  const includeRequests = args.include_requests === true;
  const collections = readCollections();
  const all = useWorkspaceStore.getState().workspaces || [];

  return {
    workspace: workspaceReport(workspace),
    // A COUNT, never names: the honest analogue of `scopesUnavailable` on a
    // surface with no scopes. Non-zero means this answer covers one workspace
    // of several, and the agent should say so rather than concluding the
    // user has nothing else.
    otherWorkspaceCount: Math.max(0, all.length - 1),
    collections: collections.map((c) => ({
      id: String(c.id),
      rev: revOfCollection(c),
      name: String(c.name || ''),
      description: typeof c.description === 'string' ? c.description : '',
      folders: foldersOf(c),
      requestCount: (c.requests || []).length,
      ...(includeRequests ? { requests: requestRowsOf(c) } : {}),
    })),
    // Present and empty on purpose — collections are workspace-scoped, so
    // there are no scopes to consult. See the header.
    scopesConsulted: [],
    scopesUnavailable: [],
  };
}
